import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { TabContext, TabList } from '@mui/lab';
import { Box, Divider, IconButton, Tab } from '@mui/material';
import classNames from 'classnames';

import Link from 'Lib/components/common/Link';
import Select from 'Lib/components/form/Select';
import SidebarContext from 'Lib/contexts/SidebarContext';
import SidebarButtons from 'Lib/muiComponents/sidebar';
import isEmpty from 'Lib/utilities/isEmpty';
import isFunction from 'Lib/utilities/isFunction';
import isObject from 'Lib/utilities/isObject';
import { screenSize } from 'Lib/utilities/screenSize';
import userDevice from 'Lib/utilities/userDevice';

const PageNav = ({
	actions,
	apis,
	children,
	components,
	common,
	current,
	forceActionDropDown = false,
	hasPermission,
	history,
	mui,
	renderActions: renderPageActions,
	setTab,
	sidebar = {},
	...rest
}) => {
	let { options } = rest;
	const [selectedOptionValue, setSelectedOptionValue] = useState(null);

	const { mobile: isMobileDevice } = userDevice();
	const { mobile } = screenSize();

	const dispatch = useDispatch();

	useEffect(() => {
		const currentURL = history.location.pathname;

		// TODO: This will probably work for 90% of the cases but may fail in others. IE 'work-orders' and 'recurring-work-orders' both match.
		const selectedOption = options.find(option => {
			if (!isEmpty(option.url)) return currentURL === option.url;
			else if (currentURL.includes(option.value)) return true;
		})?.value;

		setSelectedOptionValue(selectedOption || current);
	}, [current, history.location.pathname, options]);

	const onClick = option => {
		if (option.onClick) option.onClick(option.value);
		else setTab(option.value, option);

		setSelectedOptionValue(option.value);
	};

	const handleChange = (__, newValue) => {
		const option = options.find(option => option.value === newValue);
		onClick(option);
	};

	const renderActions = () => {
		let action = null;

		if (isFunction(renderPageActions)) return renderPageActions();

		if (!actions) return null;

		let actionList = [];

		if (isFunction(actions)) actionList = actions();
		else actionList = actions;

		if (!actionList || isEmpty(actionList)) return null;

		if (actionList.length == 1 && forceActionDropDown == false) {
			action = actionList[0];
			const { color = 'primary' } = action;

			if (action.to) {
				const { disabled, to, ...rest } = action;

				return (
					<Link to={to} {...rest}>
						<Button color={color} disabled={disabled}>
							{action.icon ? (
								<>
									<i className={`${action.icon}`}></i>&nbsp;&nbsp;
								</>
							) : null}
							{action.label}
						</Button>
					</Link>
				);
			}

			if (action.onClick) {
				const { color = 'primary', disabled } = action;

				return (
					<Button onClick={action.onClick} color={color} disabled={disabled}>
						{action.icon ? (
							<>
								<i className={`${action.icon}`}></i>&nbsp;&nbsp;
							</>
						) : null}
						{action.label}
					</Button>
				);
			}
		} else {
			return (
				<UncontrolledDropdown>
					<DropdownToggle caret color="primary">
						Actions
					</DropdownToggle>
					<DropdownMenu>
						{actionList.map((action, index) => {
							if (action.divider) {
								return <DropdownItem key={index} divider />;
							}

							if (action.to) {
								const { to, ...rest } = action;

								return (
									<Link key={index} to={to} {...rest}>
										<DropdownItem>{action.label}</DropdownItem>
									</Link>
								);
							}

							if (action.onClick) {
								return (
									<DropdownItem key={index} onClick={action.onClick}>
										{action.label}
									</DropdownItem>
								);
							}
						})}
					</DropdownMenu>
				</UncontrolledDropdown>
			);
		}
	};

	const customSelectMenuStyling = {
		menu: provided => ({
			...provided,
			top: '80%',
			width: '93%'
		})
	};

	if (!options) {
		return null;
	}

	if (isFunction(options)) {
		options = options();
	}

	return mui ? (
		<TabContext value={current}>
			<Box display="flex" justifyContent="space-between">
				<TabList
					data-cy="page-tab-list-mui"
					onChange={handleChange}
					variant="scrollable"
					scrollButtons="auto"
					allowScrollButtonsMobile={!isMobileDevice}
					indicatorColor=""
					sx={{ mx: mobile ? 0 : 4 }}>
					{options.map((option, index) => option?.isVisible && <Tab key={index} label={option.label} value={option.value} />)}
				</TabList>

				{sidebar.items && !mobile && (
					<Box display="flex" mr={2}>
						{sidebar.items.map((item, index) => {
							if (typeof item === 'object') {
								return (
									<IconButton variant="regular" key={index} onClick={item.onclick} title={item.title} sx={{ mx: 2 }}>
										{item.icon}
									</IconButton>
								);
							} else {
								const Button = SidebarButtons[item];

								if (item === 'divider') {
									return <Divider key={index} orientation="vertical" variant="middle" sx={{ ml: 2, mr: 2 }} flexItem />;
								}

								return (
									<SidebarContext.Provider value={{ sidebar, apis, components, hasPermission }} key={index}>
										<Button key={index} toggleDrawer={actionType => dispatch(common.actions.toggleDrawer(actionType))} />
									</SidebarContext.Provider>
								);
							}
						})}
					</Box>
				)}
			</Box>
		</TabContext>
	) : (
		<div data-cy="page-tab-list" className="page-nav">
			{!mobile && (
				<ul>
					{options.map(
						(option, index) =>
							option?.isVisible && (
								<li key={index} className={classNames({ active: current == option.value })} onClick={() => onClick(option)}>
									{option.label}
								</li>
							)
					)}
				</ul>
			)}

			{mobile && (
				<Select
					className=" tab-select"
					isClearable={false}
					onChange={e => onClick(e)}
					styles={customSelectMenuStyling}
					options={options
						.filter(option => option.isVisible)
						.map(option => ({
							...option,
							label: isObject(option.label) ? option.value.charAt(0).toUpperCase() + option.value.slice(1) : option.label,
							value: option.value
						}))}
					value={options.filter(option => option.value === selectedOptionValue)[0]?.value}
				/>
			)}

			<div className="page-header-actions">{children ? children : renderActions()}</div>
		</div>
	);
};

export default withRouter(PageNav);
